// src/components/MapProducts.jsx
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { base_url } from '../../utils/config';

export default function MapProducts() {
  const [shops, setShops] = useState([]);
  const navigate = useNavigate();

  // 1) Get userInfo from Redux (like in Details.jsx)
  const { userInfo } = useSelector((state) => state.auth);
  const userId = userInfo ? userInfo.id : null;
  const userName = userInfo ? userInfo.name : 'Guest User';

  console.log('[MapProducts] userId:', userId);
  console.log('[MapProducts] userName:', userName);

  // 2) Fetch shops on mount
  useEffect(() => {
    fetch(`${base_url}/api/admin/mapshop`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch map shops');
        }
        return res.json();
      })
      .then(async (baseShops) => {
        // For each shop, get minPrice, maxPrice, etc. & update color
        const updatedShops = await Promise.all(
          baseShops.map(async (shop) => {
            try {
              const resp = await fetch(
                `${base_url}/api/product/shop-info/${encodeURIComponent(shop.shopName)}`
              );
              const info = await resp.json(); // { minPrice, maxPrice, categories }
              const combined = { ...shop, ...info };

              // Compute color
              const color = computeColorForMaxPrice(combined.maxPrice || 0);

              // If DB's color differs from computed, patch it
              if (color && color !== combined.color) {
                await updateShopColorInDB(shop._id || shop.id, color);
                combined.color = color;
              }

              return combined;
            } catch (err) {
              console.error('Error in mapshop info or color update:', err);
              return shop; // fallback
            }
          })
        );
        setShops(updatedShops);
      })
      .catch((err) => console.error('Error fetching map shops:', err));
  }, []);

  // Decide color based on maxPrice
  function computeColorForMaxPrice(maxPrice) {
    if (maxPrice < 3001) {
      return '#d4f8d4'; // light green
    } else if (maxPrice < 5001) {
      return '#ffe4e1'; // light pink
    } else {
      return '#ffc2c2'; // light red
    }
  }

  // PATCH route to update a shop's color
  async function updateShopColorInDB(shopId, color) {
    try {
      await fetch(`${base_url}/api/admin/mapshop/${shopId}/color`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ color })
      });
    } catch (err) {
      console.error('Failed to update shop color in DB:', err);
    }
  }

  // Create custom Leaflet icon
  function createLabelIcon(shop) {
    const labelColor = shop.color || '#ffe4e1';
    return L.divIcon({
      html: `
        <div 
          style="
            display: inline-block;
            padding: 4px 8px;
            background: ${labelColor};
            border: 2px solid #ffb6c1;
            border-radius: 6px;
            color: #333;
            font-size: 14px;
            font-weight: bold;
            white-space: nowrap;
            transform: translate(-50%, -100%);
          "
        >
          🛍️ ${shop.shopName}
        </div>
      `,
      className: '',
      iconSize: [70, 28],
      iconAnchor: [35, 28]
    });
  }

  // 3) Handle tapping the shop icon (Marker).
  const handleShopIconClick = async (shop) => {
    if (!userId) {
      console.log('[MapProducts] No userId - skipping iconTap tracking');
      return;
    }
    const requestBody = {
      userName,
      sellerId: shop.sellerId,
      shopName: shop.shopName,
      action: 'iconTap' // <--- differentiate the action
    };
    console.log('[MapProducts] Tapped shop icon, sending body:', requestBody);

    try {
      const response = await fetch(`${base_url}/api/map-activity/${userId}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      console.log('[MapProducts] iconTap response:', data);
    } catch (err) {
      console.error('[MapProducts] Error tracking iconTap:', err);
    }
  };

  // 4) Handle "Go to Shop" button inside the Popup
  const handleGoToShop = async (sellerId, shopName) => {
    if (!sellerId) {
      console.log('[MapProducts] sellerId is null, skipping...');
      return;
    }

    if (userId) {
      const requestBody = {
        userName,
        sellerId,
        shopName,
        action: 'goToShop' // <--- differentiate the action
      };
      console.log('[MapProducts] "Go to Shop" clicked, sending body:', requestBody);

      try {
        const response = await fetch(`${base_url}/api/map-activity/${userId}/click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        console.log('[MapProducts] "Go to Shop" response:', data);
      } catch (err) {
        console.error('[MapProducts] Error tracking "Go to Shop":', err);
      }
    } else {
      console.log('[MapProducts] No userId - skipping "Go to Shop" tracking');
    }

    // Then navigate to the shop
    navigate(`/shops?seller=${sellerId}`);
  };

  return (
    <div
      style={{
        width: '90%',
        height: '50vh',
        margin: '1rem auto',
        position: 'relative',
        zIndex: 1,
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}
    >
      <MapContainer
        center={[18.9527265, 72.83243574873167]}
        zoom={16}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">
            OpenStreetMap
          </a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {shops.map((shop) => (
          <Marker
            key={shop._id || shop.id}
            position={[shop.lat, shop.lng]}
            icon={createLabelIcon(shop)}
            eventHandlers={{
              click: () => handleShopIconClick(shop)
            }}
          >
            <Popup>
              <div style={{ padding: '4px', fontFamily: 'Helvetica, sans-serif' }}>
                <h3 style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '16px' }}>
                  {shop.shopName}
                </h3>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Lat: {shop.lat}, Lng: {shop.lng}
                </p>
                <p style={{ fontWeight: 'bold', marginTop: '6px', fontSize: '14px' }}>
                  Description:
                </p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Price Range: {shop.minPrice} - {shop.maxPrice}
                </p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Categories:{' '}
                  {shop.categories?.length ? shop.categories.join(', ') : 'N/A'}
                </p>
                <button
                  onClick={() => handleGoToShop(shop.sellerId, shop.shopName)}
                  style={{
                    marginTop: '8px',
                    backgroundColor: '#4caf50',
                    color: '#fff',
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Go to Shop
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
